import { useState, FormEvent, ChangeEvent, useEffect, useRef, DragEvent } from 'react';
import { BatchUpload } from './BatchUpload';

interface UploadFormProps {
  onUpload: (file: File, profile: string) => void;
  loading: boolean;
  error: string | null;
}

export function UploadForm({ onUpload, loading, error }: UploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [profile, setProfile] = useState('iab-baseline');
  const [isDragging, setIsDragging] = useState(false);
  const [dragError, setDragError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Prevent browser's default drag and drop behavior globally
  useEffect(() => {
    const preventDefaults = (e: globalThis.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Prevent file downloads when dragging over the page
    window.addEventListener('dragover', preventDefaults);
    window.addEventListener('drop', preventDefaults);

    return () => {
      window.removeEventListener('dragover', preventDefaults);
      window.removeEventListener('drop', preventDefaults);
    };
  }, []);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (file) {
      onUpload(file, profile);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragError(null);

    const droppedFiles = Array.from(e.dataTransfer.files);

    // Check for .zip files
    const zipFiles = droppedFiles.filter(f => f.name.endsWith('.zip'));
    const nonZipFiles = droppedFiles.filter(f => !f.name.endsWith('.zip'));

    if (nonZipFiles.length > 0) {
      setDragError(`Only .zip files are allowed. ${nonZipFiles.length} file(s) were ignored.`);
      setTimeout(() => setDragError(null), 4000);
    }

    if (zipFiles.length > 0) {
      setFile(zipFiles[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setDragError(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="upload-form">
      <form onSubmit={handleSubmit}>
        {/* Drop Zone */}
        <div
          className="form-group"
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => { if (!loading && !file) fileInputRef.current?.click(); }}
          style={{
            border: `2px dashed ${isDragging ? '#3b82f6' : 'var(--input-border)'}`,
            borderRadius: '12px',
            padding: file ? '24px 20px' : '40px 20px',
            textAlign: 'center',
            cursor: loading ? 'not-allowed' : (file ? 'default' : 'pointer'),
            backgroundColor: isDragging ? 'var(--bg-tertiary)' : 'var(--bg-tertiary)',
            transition: 'all 0.2s ease',
            position: 'relative',
            animation: loading ? 'pulse-border 2s infinite' : 'none'
          }}
        >
          <style>{`
            @keyframes pulse-border {
              0%, 100% { opacity: 0.3; }
              50% { opacity: 0.7; }
            }
          `}</style>
          <input
            ref={fileInputRef}
            id="file-input"
            type="file"
            accept=".zip"
            onChange={handleFileChange}
            disabled={loading}
            style={{ display: 'none' }}
          />
          {file ? (
            <div style={{ margin: 0 }}>
              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>✓</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#10b981' }}>
                    {file.name}
                  </p>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  disabled={loading}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = '#fecaca')}
                  onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = '#fee2e2')}
                >
                  Remove
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  disabled={loading}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: '#e0e7ff',
                    color: '#3730a3',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = '#c7d2fe')}
                  onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = '#e0e7ff')}
                >
                  Replace
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '40px', margin: '0 0 8px 0' }}>📦</p>
              <p style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 4px 0', color: 'var(--text-primary)' }}>
                Drag & drop your HTML5 creative ZIP here
              </p>
              <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>
                or click to browse
              </p>
            </div>
          )}
        </div>

        {/* Drag Error Message */}
        {dragError && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#991b1b',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '16px' }}>⚠</span>
            <span>{dragError}</span>
          </div>
        )}

        <div className="form-group" style={{ marginTop: '20px' }}>
          <label htmlFor="profile-select" style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: 'var(--text-primary)' }}>
            Validation Profile
          </label>
          <select
            id="profile-select"
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
            disabled={loading}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: loading ? 'not-allowed' : 'pointer',
              backgroundColor: 'var(--input-bg)',
              color: 'var(--text-primary)',
              transition: 'border-color 0.2s'
            }}
          >
            <option value="iab-baseline">IAB Baseline</option>
            <option value="google-like">Google-like</option>
          </select>
        </div>

        {error && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#991b1b',
            fontSize: '13px'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!file || loading}
          style={{
            width: '100%',
            marginTop: '20px',
            padding: '12px 16px',
            backgroundColor: !file || loading ? '#d1d5db' : '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: !file || loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            opacity: !file || loading ? 0.6 : 1
          }}
          onMouseOver={(e) => !(!file || loading) && (e.currentTarget.style.backgroundColor = '#2563eb')}
          onMouseOut={(e) => !(!file || loading) && (e.currentTarget.style.backgroundColor = '#3b82f6')}
        >
          {loading ? '⏳ Validating...' : 'Validate Creative'}
        </button>
      </form>

      {/* Batch Upload Section */}
      <BatchUpload />
    </div>
  );
}
